import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import type { TrialResearchPreview, TrialResearchStatus } from '../../types';
import { industryLabelFromPreview } from '../../utils/trial-industry';
import { trialLocationNames, trialLocationSummary } from '../../utils/trial-locations';
import {
  colorsFromPreview,
  resolveTrialLogoSources,
  trialLogoDisplayMode,
} from '../../utils/trial-preview';
import {
  deriveResearchFindings,
  researchScanDisplayPercent,
  scanProgressPercentFromProgress,
  scanStatusKeyFromProgress,
  tickMonotonicScanPercent,
} from '../../utils/trial-research-progress';
import {
  elapsedScanBarPercent,
  mergeScanBarDisplayPercent,
  scanProgressPercent,
  scanStatusKey,
} from '../../utils/trial-scan-status';
import { BrandLogo } from './brand-logo';
import { TrialArrow } from './trial-arrow';
import { TRIAL_WORDMARK } from './trial-media';

type Props = {
  domain: string;
  research: TrialResearchStatus | null;
  preview: TrialResearchPreview | null;
  isDone: boolean;
  onContinue: () => void;
};

export const ScanStep = memo(({
  domain, research, preview, isDone, onContinue,
}: Props) => {
  const { t } = useTranslation();
  const colors = preview ? colorsFromPreview(preview) : null;
  const locationNames = trialLocationNames(preview?.locations);
  const locationSummary = trialLocationSummary(locationNames);
  const ownImages = Array.from(new Set(
    [preview?.screenshot_url, ...(preview?.images ?? [])].filter((url): url is string => !!url),
  ));
  const isFetchingSite = ownImages.length === 0;
  const findings = useMemo(() => deriveResearchFindings(research, t), [research, t]);
  const progressSteps = research?.progress?.steps;
  const findingsPct = progressSteps?.length
    ? scanProgressPercentFromProgress(progressSteps, isDone)
    : scanProgressPercent(findings, isDone);
  const statusKey = progressSteps?.length
    ? scanStatusKeyFromProgress(research?.progress, isDone)
    : scanStatusKey(findings, isDone);
  const [elapsedPct, setElapsedPct] = useState(0);
  const [smoothPct, setSmoothPct] = useState(0);

  useEffect(() => {
    if (isDone) {
      setElapsedPct(100);
      return undefined;
    }

    setElapsedPct(0);
    setSmoothPct(0);

    const start = performance.now();
    const tick = () => {
      setElapsedPct(elapsedScanBarPercent((performance.now() - start) / 1000));
    };

    tick();
    const id = window.setInterval(tick, 100);

    return () => window.clearInterval(id);
  }, [isDone, domain]);

  const clientPct = mergeScanBarDisplayPercent(findingsPct, elapsedPct, isDone);
  const targetPct = researchScanDisplayPercent(research, isDone, clientPct);

  useEffect(() => {
    if (isDone) {
      setSmoothPct(100);
      return undefined;
    }

    const id = window.setInterval(() => {
      setSmoothPct((current) => tickMonotonicScanPercent(current, targetPct));
    }, 100);

    return () => window.clearInterval(id);
  }, [isDone, targetPct]);

  const displayPct = isDone ? 100 : smoothPct;
  const displayName = preview?.name?.trim() || domain;
  const hasBrandAssets = !!(preview?.logo_url || preview?.icon_url || preview?.name?.trim());
  const industryDisplayLabel = preview && (preview.industry_label?.trim() || preview.industry_proposed?.trim())
    ? industryLabelFromPreview(preview, t)
    : null;
  const extractStepDone = research?.progress?.steps?.some(
    (step) => step.key === 'extract' && (step.status === 'done' || step.status === 'failed'),
  );
  const scanLogoDisplay = preview ? trialLogoDisplayMode(preview) : null;
  const scanLogoSources = preview ? resolveTrialLogoSources(preview, { logoUrl: null }) : null;

  return (
    <div
      className={
        'FreeTrial__Scan max-md:!tw-flex max-md:!tw-flex-col max-md:!tw-gap-3 '
        + 'max-md:!tw-px-4 max-md:!tw-pb-6 max-md:!tw-pt-5'
      }
    >
      <img className="FreeTrial__Scan__Mark max-md:tw-block tw-hidden" src={TRIAL_WORDMARK} alt="" />
      <div className="FreeTrial__Scan__Head max-md:!tw-hidden">
        <h1>{t('authentication:trial_scan_heading', { domain })}</h1>
      </div>

      <div
        className={
          'FreeTrial__Scan__Stage max-md:!tw-m-0 max-md:!tw-grid max-md:!tw-min-h-0 '
          + 'max-md:!tw-grid-cols-2 max-md:!tw-gap-2.5'
        }
      >
        <div className="FreeTrial__Browser max-md:!tw-col-span-2 max-md:!tw-h-[178px] max-md:!tw-w-full">
          <div className="FreeTrial__Browser__Chrome">
            <span /><span /><span />
            <em>{`https://${domain}`}</em>
            <span
              className={`FreeTrial__Browser__ChromePct${isDone ? ' is-done' : ''}`}
              aria-live="polite"
            >
              {isDone ? (
                <svg
                  className="FreeTrial__Browser__ChromePctCheck"
                  aria-hidden="true"
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2463eb"
                  strokeWidth="3.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12l5 5 9-10" />
                </svg>
              ) : (
                <span className="FreeTrial__Browser__ChromePctSpin" aria-hidden="true" />
              )}
              <span className="FreeTrial__Browser__ChromePctValue">{displayPct}%</span>
            </span>
          </div>
          <div
            className="FreeTrial__Browser__Progress"
            role="progressbar"
            aria-label={t('authentication:trial_scan_heading', { domain })}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-busy={!isDone || undefined}
            aria-valuenow={displayPct}
          >
            <span
              className={isDone ? 'is-done' : undefined}
              style={{ width: `${displayPct}%` }}
            />
          </div>
          <div className="FreeTrial__Browser__Page">
            <div className="FreeTrial__Browser__Nav">
              <div className="FreeTrial__Browser__NavLogo">
                <BrandLogo
                  variant={scanLogoDisplay?.variant ?? 'onLight'}
                  iconUrl={scanLogoSources?.iconUrl}
                  logoUrl={scanLogoSources?.logoUrl}
                  name={displayName}
                />
              </div>
              <span /><span /><span />
            </div>
            {isFetchingSite ? (
              <p className="FreeTrial__Browser__Fetching">{t('authentication:trial_scan_fetching')}</p>
            ) : (
              <>
                <div
                  className="FreeTrial__Browser__Hero"
                  style={{
                    backgroundColor: ownImages[0] ? undefined : (colors?.main || '#2463eb'),
                    backgroundImage: ownImages[0] ? `url(${ownImages[0]})` : undefined,
                  }}
                >
                  {!ownImages[0] && (
                    <>
                      <i /><i /><b style={{ backgroundColor: colors?.button || '#fff800' }} />
                    </>
                  )}
                </div>
                <div className="FreeTrial__Browser__Tiles">
                  {ownImages.slice(1, 5).map((src) => (
                    <span key={src} style={{ backgroundImage: `url(${src})` }} />
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="FreeTrial__Browser__Scanline" />
        </div>

        {preview && hasBrandAssets && (
          <aside className="FreeTrial__FindingCard FreeTrial__FindingCard--logo">
            <div className="FreeTrial__FindingCard__Head">
              <strong>{t('authentication:trial_scan_logo')}</strong>
            </div>
            <div className="FreeTrial__FindingCard__Logo">
              <BrandLogo
                variant={scanLogoDisplay?.variant ?? 'onLight'}
                iconUrl={scanLogoSources?.iconUrl}
                logoUrl={scanLogoSources?.logoUrl}
                name={displayName}
              />
            </div>
          </aside>
        )}

        {preview?.colors && (
          <aside className="FreeTrial__FindingCard FreeTrial__FindingCard--colours">
            <div className="FreeTrial__FindingCard__Head">
              <strong>{t('authentication:trial_scan_colours')}</strong>
            </div>
            <div className="FreeTrial__FindingCard__Swatches">
              <i style={{ backgroundColor: preview.colors.main }} />
              <i style={{ backgroundColor: preview.colors.button }} />
            </div>
          </aside>
        )}

        {(industryDisplayLabel || extractStepDone) && (
          <aside className="FreeTrial__FindingCard FreeTrial__FindingCard--industry">
            <div className="FreeTrial__FindingCard__Head">
              <strong>{t('authentication:trial_scan_industry')}</strong>
            </div>
            {industryDisplayLabel ? <p>{industryDisplayLabel}</p> : null}
          </aside>
        )}

        {(locationSummary || isDone) && (
          <aside className="FreeTrial__FindingCard FreeTrial__FindingCard--locations">
            <div className="FreeTrial__FindingCard__Head">
              <strong>{t('authentication:trial_scan_locations')}</strong>
            </div>
            <p>{locationSummary || t('authentication:trial_scan_locations_sample')}</p>
          </aside>
        )}

        {preview?.mission_statement && (
          <aside className="FreeTrial__FindingCard FreeTrial__FindingCard--mission max-md:!tw-col-span-2">
            <div className="FreeTrial__FindingCard__Meta">
              <strong>{t('authentication:trial_scan_mission_label')}</strong>
            </div>
            <p>{preview.mission_statement}</p>
          </aside>
        )}
      </div>

      <div className="FreeTrial__Scan__Foot max-md:!tw-col-span-2 max-md:!tw-mt-2">
        {!isDone ? (
          <p className="FreeTrial__Scan__Status" role="status" aria-live="polite">
            <span className="FreeTrial__Scan__StatusDot" aria-hidden="true" />
            {t(statusKey)}
          </p>
        ) : (
          <>
            <p>{t('authentication:trial_scan_ready')}</p>
            <Button type="primary" onClick={onContinue}>
              <span className="FreeTrial__Cta">
                {t('authentication:trial_preview_continue')}
                <TrialArrow />
              </span>
            </Button>
          </>
        )}
      </div>
    </div>
  );
});
